import { appInsights } from '../services/appinsights.service';
import { NOTIFICATIONS, NOTIFICATION_TYPE } from './notifications';
import { AUCTION_EVENTS } from '../event_bus_modules/auction_bidding';
import {
  DEFAULT_TIMEOUT,
  getMaxBidMetOrExceededTranslatedMessage,
} from './utils';

const createClientHandlers = ({
  userBidderId,
  auctionPropertyId,
  stopBidRetry,
}) => {
  let nextBidAmount = 0;

  // sets nextBidAmount, to be used in invalidMaxBid handler
  EVENT_BUS.on(AUCTION_EVENTS.AUCTION_STATUS_UPDATED, ({ nextAmount }) => {
    nextBidAmount = nextAmount;
  });

  const bidResponse = (messageResponse) => {
    const {
      BidAmount,
      BidderId,
      BidPlacedDateTime,
      IsReserveMet,
      IsClosed,
      Bids,
      BidderIds,
      SecondsToClosing,
      AuctionPropertyId,
      NextBidAmount,
      IsInExtendedPeriod,
    } = messageResponse;

    // dispatch global event about bid changed
    EVENT_BUS.dispatch(AUCTION_EVENTS.AUCTION_STATUS_UPDATED, {
      amount: BidAmount,
      nextAmount: NextBidAmount,
      bidderId: BidderId,
      bidderIds: BidderIds,
      bidTime: BidPlacedDateTime,
      reserveMet: IsReserveMet,
      isClosed: IsClosed,
      bids: Bids,
      secondsToClosing: SecondsToClosing,
      userBidderId,
      isInExtendedPeriod: IsInExtendedPeriod,
      timeout: DEFAULT_TIMEOUT,
    });

    if (BidderId === userBidderId) {
      appInsights.trackEvent({
        name: 'bids.client.bidResponse',
        properties: {
          AuctionPropertyId: AuctionPropertyId,
          Message: BidAmount,
          BidderId: BidderId,
        },
      });
    }
  };

  const autoIncrementPlaced = (
    maxBidString,
    responseAuctionPropertyId,
    maxBid
  ) => {
    if (responseAuctionPropertyId === auctionPropertyId) {
      stopBidRetry();
      EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
        type: NOTIFICATION_TYPE.SUCCESS,
        notification: NOTIFICATIONS.AUTO_INCREMENT_PLACED,
        message: maxBidString,
        maxBid,
      });
    }
  };

  const bidPlacedWhilstReserveMetAndHighestBidder = (message) => {
    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.SUCCESS,
      notification:
        NOTIFICATIONS.BID_PLACED_WHILE_RESERVE_MET_AND_HIGHEST_BIDDER,
      message,
    });
    appInsights.trackEvent({
      name: 'BidPlacedWhilstReserveMetAndHighestBidder',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        Message: message,
        BidderId: userBidderId,
      },
    });
    stopBidRetry();
  };

  const bidPlaceAttemptByNonBidder = (message) => {
    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.ERROR,
      notification: NOTIFICATIONS.BID_PLACE_ATTEMPT_BY_NON_BIDDER,
      message,
    });
    appInsights.trackEvent({
      name: 'BidPlaceAttemptByNonBidder',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        Message: message,
        BidderId: userBidderId,
      },
    });
    stopBidRetry();
  };

  const errorProcessingBid = (message) => {
    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.WARNING,
      notification: NOTIFICATIONS.ERROR_PROCESSING_BID,
      message,
    });
    appInsights.trackEvent({
      name: 'ErrorProcessingBid',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        Message: message,
        BidderId: userBidderId,
      },
    });
    stopBidRetry();
  };

  const bidLotClosedBidRejectedResponse = (response) => {
    const { Message, LotId, LotCloseTime, Username } = response;

    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.WARNING,
      notification: NOTIFICATIONS.LOT_CLOSED_BID_REJECTED,
      message: Message,
    });
    appInsights.trackEvent({
      name: 'BidLotClosedBidRejected',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        BidderId: userBidderId,
        LotCloseTime,
        Username,
        Message,
        LotId,
      },
    });
    stopBidRetry();
  };

  const bidLotExpiredResponse = (response) => {
    const { Message } = response;

    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.ERROR,
      notification: NOTIFICATIONS.BID_LOT_EXPIRED,
      message: Message,
    });
    appInsights.trackEvent({
      name: 'BidLotExpired',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        BidderId: userBidderId,
        Message,
      },
    });
    stopBidRetry();
  };

  const autoIncrementExpired = (message) => {
    const {
      CancelAtAmount: cancelAtAmount,
      LotNumber: lotNumber,
      AuctionPropertyId: responseAuctionPropertyId,
    } = message;

    // show notification of expired lot on every lot for the user
    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.WARNING,
      notification: NOTIFICATIONS.MAX_BID_MET_OR_EXCEEDED,
      message: { cancelAtAmount, lotNumber },
    });

    // trigger appInsights and UI state changes only if auto bid expires for current lot
    if (auctionPropertyId === responseAuctionPropertyId) {
      EVENT_BUS.dispatch(AUCTION_EVENTS.AUTO_BID_EXPIRED);

      const translatedMessage = getMaxBidMetOrExceededTranslatedMessage();
      const messageWithData = translatedMessage
        .replaceAll('[CancelAtAmount]', cancelAtAmount)
        .replaceAll('[LotNumber]', lotNumber);

      appInsights.trackEvent({
        name: 'MaxBidMetOrExceeded',
        properties: {
          AuctionPropertyId: auctionPropertyId,
          Message: messageWithData,
          BidderId: userBidderId,
        },
      });
    }
  };

  const invalidBidAttempted = (message) => {
    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.WARNING,
      notification: NOTIFICATIONS.INVALID_BID_ATTEMPT,
      message,
    });
    appInsights.trackEvent({
      name: 'InvalidBidAttempt',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        Message: message,
        BidderId: userBidderId,
      },
    });
  };

  const warmUpClient = () => {
    // no action
  };

  const autoIncrementPlacedWhileHighestBidder = (
    message,
    responseAuctionPropertyId
  ) => {
    if (responseAuctionPropertyId === auctionPropertyId) {
      EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
        type: NOTIFICATION_TYPE.WARNING,
        notification: NOTIFICATIONS.AUTO_INCREMENT_PLACED_WHILE_HIGHEST_BIDDER,
        message,
      });
      appInsights.trackEvent({
        name: 'AutoIncrementPlacedWhileHighestBidder',
        properties: {
          AuctionPropertyId: auctionPropertyId,
          Message: 'AutoIncrementPlacedWhileHighestBidder',
          BidderId: userBidderId,
        },
      });
      stopBidRetry();
    }
  };

  const unexpiredAutoIncrementAlreadyExists = (message) => {
    const { AuctionPropertyId: responseAuctionPropertyId } = message;

    if (auctionPropertyId === responseAuctionPropertyId) {
      EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
        type: NOTIFICATION_TYPE.WARNING,
        notification: NOTIFICATIONS.UNEXPIRED_AUTO_INCREMENT_ALREADY_EXIST,
        message,
      });
      appInsights.trackEvent({
        name: 'UnexpiredAutoIncrementAlreadyExists',
        properties: {
          AuctionPropertyId: auctionPropertyId,
          Message: 'UnexpiredAutoIncrementAlreadyExists',
          BidderId: userBidderId,
        },
      });
      stopBidRetry();
    }
  };

  const invalidMaxBid = () => {
    EVENT_BUS.dispatch(AUCTION_EVENTS.SHOW_NOTIFICATION, {
      type: NOTIFICATION_TYPE.WARNING,
      notification: NOTIFICATIONS.INVALID_MAX_BID,
      message: { nextBidAmount },
    });
    appInsights.trackEvent({
      name: 'InvalidMaxBid',
      properties: {
        AuctionPropertyId: auctionPropertyId,
        Message: nextBidAmount,
        BidderId: userBidderId,
      },
    });
    stopBidRetry();
  };
  return {
    autoIncrementExpired,
    autoIncrementPlaced,
    autoIncrementPlacedWhileHighestBidder,
    bidLotClosedBidRejectedResponse,
    bidLotExpiredResponse,
    bidPlaceAttemptByNonBidder,
    bidPlacedWhilstReserveMetAndHighestBidder,
    bidResponse,
    errorProcessingBid,
    invalidBidAttempted,
    invalidMaxBid,
    unexpiredAutoIncrementAlreadyExists,
    warmUpClient,
  };
};

export default createClientHandlers;
